import React from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import MarketingPublicNav from '../components/MarketingPublicNav';
import { PublicBandSection, PublicMarketingLayout } from '../components/PublicMarketingLayout';

const Privacy: React.FC = () => {
  return (
    <PublicMarketingLayout>
      <MarketingPublicNav />

      <PublicBandSection band={1} className="px-6 pb-8 pt-16">
        <div className="mx-auto max-w-4xl">
          <h1 className="mb-4 bg-gradient-to-r from-violet-500 to-blue-500 bg-clip-text text-5xl font-bold text-transparent">
            Privacy Policy
          </h1>
          <p className="text-gray-400">Last updated: September 17, 2026</p>
        </div>
      </PublicBandSection>

      <PublicBandSection band={2} className="px-6 pb-16 pt-4">
        <div className="mx-auto max-w-4xl">
        <div className="space-y-12">
          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">1. Information We Collect</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              At kol.my, we collect information you provide directly to us, including:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-400">
              <li>Account information (name, email, phone number, company details)</li>
              <li>Profile information (photos, bio, social media accounts, audience demographics)</li>
              <li>Payment information (processed securely through our payment partners)</li>
              <li>Content you create on our platform</li>
              <li>Communications and messages with other users</li>
              <li>Usage data and analytics</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">2. The kol.my Insight browser extension</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              kol.my Insight is an optional Chrome extension used by agency staff. It reads a
              creator&rsquo;s public profile page <strong>only when you click Capture</strong>, on the
              tab you are already looking at. It does not read pages in the background, does not
              follow you as you browse, and has no access to your browsing history.
            </p>

            <h3 className="text-lg font-semibold mb-2 mt-6 text-white">What it sends to kol.my</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-400">
              <li>
                Public profile information from the creator page you captured &mdash; handle, display
                name, profile picture, follower, following and post counts, and where the platform
                shows them, public like and comment counts
              </li>
              <li>
                Which platform, which version of our reader, and whether the read succeeded or
                failed. This diagnostic record contains no handle, no URL and no identifier for you
                or the creator &mdash; it exists so we can tell when a platform changes its page
                layout and our reader breaks
              </li>
            </ul>

            <h3 className="text-lg font-semibold mb-2 mt-6 text-white">What it stores on your computer</h3>
            <p className="text-gray-400 mb-4 leading-relaxed">
              Only what is needed to stay signed in: a device token for your browser, the kol.my
              address it talks to, and which agency you belong to. Nothing about the pages you
              visit is stored locally. You can revoke a browser&rsquo;s access at any time from
              Extension devices in your kol.my account, or by clicking Disconnect in the extension.
            </p>

            <h3 className="text-lg font-semibold mb-2 mt-6 text-white">What it never does</h3>
            <ul className="list-disc pl-6 space-y-2 text-gray-400">
              <li>Read any page other than the creator profile you explicitly captured</li>
              <li>Collect your browsing history, cookies, passwords, or anything behind a login</li>
              <li>Read private or followers-only accounts, or anything not publicly visible</li>
              <li>Sell data, or use it for advertising, credit scoring, or lending decisions</li>
              <li>Transfer data to anyone except kol.my, for the purpose described here</li>
            </ul>

            <p className="text-gray-400 mt-4 leading-relaxed">
              Captured public metrics may be shared with other kol.my agencies through our shared
              creator pool, if your agency has chosen to take part. Creators can see what is held
              about them and have it removed at{' '}
              <Link to="/creator-data" className="text-violet-400 hover:text-violet-300">
                kol.my/creator-data
              </Link>
              , without needing an account with us.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">3. How We Use Your Information</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              We use the information we collect to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-400">
              <li>Provide, maintain, and improve our services</li>
              <li>Process transactions and send related information</li>
              <li>Send promotional communications (with your consent)</li>
              <li>Respond to your comments, questions, and requests</li>
              <li>Monitor and analyze trends, usage, and activities</li>
              <li>Detect, investigate, and prevent fraudulent or unauthorized activities</li>
              <li>Personalize and improve your experience</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">4. Information Sharing</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except as described in this policy. We may share information with:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-400">
              <li>Other users (as necessary for platform functionality)</li>
              <li>Service providers who assist in our operations</li>
              <li>Legal authorities when required by law</li>
              <li>Business partners with your explicit consent</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">5. Data Security</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              We implement appropriate technical and organizational measures to protect your personal information, including:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-400">
              <li>SSL/TLS encryption for data transmission</li>
              <li>Secure data centers with access controls</li>
              <li>Regular security assessments and updates</li>
              <li>Employee training on data protection</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">6. Your Rights</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              Under Malaysian PDPA and GDPR (for EU users), you have the right to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-400">
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Withdraw consent at any time</li>
              <li>Data portability</li>
              <li>Object to processing</li>
            </ul>
            <p className="text-gray-400 mt-4 leading-relaxed">
              To exercise these rights, contact us at i@kol.my.
            </p>
            <p className="text-gray-400 mt-4 leading-relaxed">
              If you are a creator and your public profile has been looked up by an agency using
              kol.my — whether or not you have an account here —{' '}
              <Link to="/creator-data" className="text-violet-400 hover:text-violet-300">
                see what is held and have it removed
              </Link>
              . No account needed.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">7. Cookies and Tracking</h2>
            <p className="text-gray-400 mb-4 leading-relaxed">
              We use cookies and similar technologies to enhance your experience. For detailed information, see our{' '}
              <Link to="/cookies" className="text-violet-400 hover:underline">Cookie Policy</Link>.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">8. Data Retention</h2>
            <p className="text-gray-400 leading-relaxed">
              We retain your personal information for as long as your account is active or as needed to provide services. You may request deletion of your data at any time by contacting us.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">9. Changes to This Policy</h2>
            <p className="text-gray-400 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-4 text-white">10. Contact Us</h2>
            <p className="text-gray-400 leading-relaxed">
              If you have any questions about this Privacy Policy, please contact us at:<br />
              <strong className="text-white">Email:</strong> i@kol.my<br />
              <strong className="text-white">Address:</strong> Suite 12.03, Level 12, Wisma UOA Pantai, 59200 Kuala Lumpur, Malaysia
            </p>
          </section>
        </div>
        </div>
      </PublicBandSection>

      <Footer />
    </PublicMarketingLayout>
  );
};

export default Privacy;
